/*
 * Cross-sell add to cart.
 *
 * These are upsells, so adding one must never navigate away from the product page the
 * customer is still configuring. The theme's own <product-form> redirects to the cart
 * whenever no cart drawer or notification element exists, which is the case while the
 * cart type is set to "page" — hence this separate, self-contained handler.
 */
if (!customElements.get('cross-sell-form')) {
  customElements.define(
    'cross-sell-form',
    class CrossSellForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        this.button = this.querySelector('.sst-cross-sell__button');
        this.label = this.querySelector('[data-cross-sell-label]');
        this.errorTarget = this.querySelector('[data-cross-sell-error]');
        this.addedLabel = this.dataset.addedLabel || 'Added to cart';

        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      onSubmit(event) {
        event.preventDefault();
        // The added state is final, so ignore repeat submits once the item is in the cart.
        if (this.button.classList.contains('is-loading') || this.added) return;

        this.setError('');
        this.button.classList.add('is-loading');
        this.button.setAttribute('aria-disabled', 'true');

        const body = new FormData(this.form);

        fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.setError(response.description || response.message || '');
              return;
            }
            this.onAdded(response);
          })
          .catch(() => {
            this.setError(window.sstCrossSellStrings?.error || 'Could not add to cart. Please try again.');
          })
          .finally(() => {
            this.button.classList.remove('is-loading');
            if (!this.added) this.button.removeAttribute('aria-disabled');
          });
      }

      onAdded(response) {
        this.refreshCartCount();

        // The confirmation is permanent: the item is in the cart and stays there.
        this.added = true;
        if (this.label) this.label.textContent = this.addedLabel;
        this.button.classList.add('is-added');
        this.button.disabled = true;

        // Let the rest of the theme (cart drawer, analytics) react to the new line item.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cross-sell-form',
            productVariantId: new FormData(this.form).get('id'),
            cartData: response,
          });
        }
      }

      // The /cart/add response describes the added line, not the whole cart, so read the
      // authoritative count from cart.js and update the header badge in place.
      refreshCartCount() {
        fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        })
          .then((response) => response.json())
          .then((cart) => {
            const count = cart.item_count;

            document.querySelectorAll('.sst-header__cart').forEach((link) => {
              let badge = link.querySelector('.sst-header__cart-count');
              if (!badge && count > 0) {
                badge = document.createElement('span');
                badge.className = 'sst-header__cart-count';
                link.appendChild(badge);
              }
              if (badge) badge.textContent = count;
              link.setAttribute('aria-label', `Cart with ${count} items`);
            });

            document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((node) => {
              node.textContent = count;
            });
          })
          .catch(() => {
            /* The item is in the cart either way; a stale badge is not worth surfacing. */
          });
      }

      setError(message) {
        if (!this.errorTarget) return;
        this.errorTarget.textContent = message;
      }
    }
  );
}
